/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/

import { v4 as uuidv4 } from "uuid";
import * as actions from ".";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { StudyCohort, LoadStatus } from "../../../types";

export interface UseWithClasses {
  classes: Record<string, StudyCohort>;
  classLoadStatus: LoadStatus;
  loadAllClasses: () => Promise<StudyCohort[]>;
  createClass: (newData: Partial<StudyCohort>) => Promise<StudyCohort>;
  deleteClass: (c: StudyCohort) => Promise<void>;
  updateClass: (newData: StudyCohort) => Promise<StudyCohort>;
}

export default function useWithClasses(): UseWithClasses {
  const { user } = useAppSelector((state) => state.login);
  const { classes, classLoadStatus } = useAppSelector((state) => state.classes);
  const dispatch = useAppDispatch();

  async function loadAllClasses(): Promise<StudyCohort[]> {
    if (classLoadStatus === LoadStatus.IN_PROGRESS) return [];
    return await dispatch(actions.loadClasses()).unwrap();
  }

  async function createClass(data: Partial<StudyCohort>): Promise<StudyCohort> {
    if (!user) throw new Error("login required");
    if (!data.name) throw new Error("name required");
    if (!data.goal) throw new Error("goal required");
    return await dispatch(
      actions.addOrUpdateClass({
        ...data,
        id: data.id,
        name: data.name,
        code: data.code || uuidv4(),
        goal: data.goal,
        blacklist: [],
        teams: [],
        members: [],
        membersPerTeam: 10,
      })
    ).unwrap();
  }

  async function deleteClass(c: StudyCohort): Promise<void> {
    await dispatch(actions.deleteClass(c.id));
  }

  async function updateClass(data: StudyCohort): Promise<StudyCohort> {
    return await dispatch(actions.addOrUpdateClass(data)).unwrap();
  }

  return {
    classes,
    classLoadStatus,
    loadAllClasses,
    createClass,
    deleteClass,
    updateClass,
  };
}
